import React, { FC, useEffect, useRef, useState } from "react";
import {
  DeleteOutlined,
  GroupOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { BomWrap } from "./style";
import {
  App,
  Button,
  Dropdown,
  Flex,
  Input,
  notification,
  Progress,
  TableProps,
  Tooltip,
  Upload,
  UploadProps
} from "antd";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef
} from "@/styles/STableLayout";
import apis, { addBom } from "@/apis/bom";
import useDataList from "@/hooks/useDataList";
import { CommonListParams } from "@/types/common";
import { Bom, BomAdd } from "@/types/bom";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { useTranslation } from "react-i18next";
import SText from "@/styles/SText";
import { BomDetail } from "./components/BomDetail";
import useDataDel from "@/hooks/useDataDel";
import useFrontDelTip from "@/hooks/useFrontDelTip";
// import BomUpdate from "./components/BomUpdate";
import { dateFormat } from "@/utils/format";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import dayjs from "dayjs";
import { makeBomData, parseBomFileBuffer, RcFile2Buffer } from "../Requirement/pages/Create/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { getUserName } from "@/utils/user";
import BomAddForm from "./components/BomAddForm";
import { SConModal } from "@/styles/SConModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

const key = "upload";
const type = "line";

interface BomFilters {
  keyword: string;
}

const INITIAL_BOM_FILTERS: BomFilters = {
  keyword: "",
};

const BomHome: FC = () => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { curUser } = useSelector((state: RootState) => state.userReducer)

  const [list, total, listLoading, { fetchList, addItem, updateItem, setList }] =
    useDataList<Bom, [CommonListParams<string>?]>({ fetchApi: apis.list });
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<BomFilters>(INITIAL_BOM_FILTERS);
  const { keyword } = draftFilters;
  const [uploadName, setUploadName] = useState<string>("");
  const [detailId, setDetailId] = useState<string>("");
  // const [bomInfo, setBomInfo] = useState<Bom | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [bomAddData, setBomAddData] = useState<BomAdd | null>(null);

  useEffect(() => {
    fetch(0, INITIAL_BOM_FILTERS);
  }, []);

  const delData = (id: string) => {
    return apis.del(id)
      .then(() => {
        setList(last => last.filter(b => b.bomId !== id))
      })
  }

  const props: UploadProps = {
    name: "file",
    accept: ".xlsx, .xls",
    fileList: [],
    disabled: !!uploadName,
    beforeUpload(info) {
      setUploadName(info.name);
    },
    async customRequest({ file }) {
      notification.open({
        key,
        message: uploadName,
        description: <Progress type={type} percent={0} key={"upload"} />,
        duration: null
      });
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await apis.importBom(formData, {
          onUploadProgress: event => {
            const percent = event.loaded / (event as { total: number })?.total;
            const progress = Math.ceil(percent * 100);
            notification.open({
              key,
              message: uploadName,
              description: (
                <Progress type={type} percent={progress} key={"upload"} />
              ),
              duration: null
            });
          }
        });
        // 业务逻辑保留
        if (list.find(item => item.productId === res.body.productId)) {
          updateItem(res.body);
        } else {
          addItem(res.body);
        }
      } catch (err) {
        const errors: { [key in number]: string } = {
          69001: t("common.serverError")
        };

        notification.error({
          placement: 'top',
          message: t("bom.importError"),
          description: errors[err as number]
            ? errors[err as number]
            : t("common.serverError")
        });
      } finally {
        setUploadName("");
      }
    }
  };

  const handleExportAll = async (): Promise<Bom[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allBomData: Bom[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await apis.list({
          pagination: { offset, take: batchSize },
          body: appliedFilters.keyword
        });

        if (response.body && response.body.length > 0) {
          allBomData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allBomData.length /
                (response.pagination?.size || allBomData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);
        } else {
          hasMore = false;
        }

        if (response.body.length < batchSize) {
          hasMore = false;
        }
      }

      setExportProgress(100);
      return allBomData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const bomColumns = [
    { key: 'bomType', label: t('bom.bomType') },
    { key: 'bomCode', label: t('bom.bomCode') },
    { key: 'bomName', label: t('bom.bomName') },
    { key: 'model', label: t('product.brand1') },
    { key: 'uom', label: t('bom.bomUnit') },
    { key: 'version', label: t('bom.bomVersion') },
    { key: 'versionDesc', label: t('bom.bomVersionDesc') },
    { key: 'status', label: t('bom.bomStatus') },
    { key: 'approvaler', label: t('bom.approvaler') },
    { key: 'createDate', label: t('bom.createDate') },
    { key: 'creator', label: t('bom.creator') },
    { key: 'updateDate', label: t('bom.updateDate') },
    { key: 'updater', label: t('bom.updater') },
  ];

  const bomDataMapper = (item: Bom, t: any) => ({
    [t('bom.bomType')]: item.type || '',
    [t('bom.bomCode')]: item.product?.code || '',
    [t('bom.bomName')]: item.product?.name || '',
    [t('product.brand1')]: item.product?.model || '',
    [t('bom.bomUnit')]: item.product?.uom?.toLocaleUpperCase() || '',
    [t('bom.bomVersion')]: item.version || '',
    [t('bom.bomVersionDesc')]: item.versionDesc || '',
    [t('bom.bomStatus')]: item.status || '',
    [t('bom.approvaler')]: item.approvaler || '',
    [t('bom.createDate')]: item.bomCreateDate ? dateFormat(dayjs(item.bomCreateDate)) : '',
    [t('bom.creator')]: item.bomCreateDateBy || '',
    [t('bom.updateDate')]: item.bomUpdateDate ? dateFormat(dayjs(item.bomUpdateDate)) : '',
    [t('bom.updater')]: item.bomUpdateDateBy || '',
  });

  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      let buffer = await RcFile2Buffer(file);
      let bomTableData = await parseBomFileBuffer(buffer, 1);
      let parseList = bomTableData.list;
      let bomData = bomTableData.header;
      let bomList = makeBomData(parseList);
      let now = Date.now();
      const addData: BomAdd = {
        type: "",
        code: bomData.code,
        name: bomData.name,
        model: bomData.model,
        unit: "",
        version: bomData.version,
        versionDesc: "",
        status: "",
        approvaler: "",
        bomDate: now,
        bomCreateDate: now,
        bomCreateDateBy: getUserName(curUser),
        bomUpdateDate: now,
        bomUpdateDateBy: getUserName(curUser),
        items: bomList.map(rb => ({
          code: rb.product.code,
          name: rb.product.name,
          model: rb.product.model,
          unit: rb.product.uom,
          baseQuantity: (rb.baseQuantity || '') + '',
          baseUnit: (rb.baseQuantity || '') + '',
          attritionRate: (rb.attritionRate || '') + '',
          useQuantity: (rb.useQuantity || '') + '',
          startDate: '',
          endDate: '',
          remark: rb.product.desc,
          locators: rb.locators ?? '',
          extraRate: 0,
          subs: [],
          process: rb.bomProcess ?? '',
          reps: (rb.reqs || []).map(rep => ({
            code: rep.product.code,
            name: rep.product.name,
            model: rep.product.model,
            unit: rep.product.uom,
            baseQuantity: (rep.baseQuantity || '') + '',
            baseUnit: (rep.baseQuantity || '') + '',
            attritionRate: (rep.attritionRate || '') + '',
            useQuantity: (rep.useQuantity || '') + '',
            startDate: '',
            endDate: '',
            remark: rep.product.desc,
            locators: rep?.locators ?? '',
            extraRate: 0,
          }))
        }))
      };
      setBomAddData({ ...addData });
      // let bomRes = await addBom(addData);
      // addItem(bomRes);
      // notification.success({ message: 'BOM' + t('app.addSuccess'), description: bomRes.product.code })
      setUploading(false);
      return false
    } catch (error) {
      setUploading(false);
      return false
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_BOM_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex justify="space-between" align="center" style={{ width: "100%" }}>
      <Flex gap={10}>
        <Dropdown trigger={['click']}
          menu={{
            items: [
              {
                key: 'NORM',
                label: (
                  <Upload {...props}>
                    <Button type='text' icon={<UploadOutlined />}>V1 BOM</Button>
                  </Upload>
                ),

              },
              {
                key: 'WARE',
                label: (
                  <Upload
                    beforeUpload={handleImport}
                    showUploadList={false}
                  >
                    <Button type='text' icon={<UploadOutlined />} loading={uploading} >V2 BOM</Button>
                  </Upload>
                ),
              },
            ],
          }}
        >
          <Button loading={uploading} icon={<UploadOutlined />}>{t("bom.bomUpload")}</Button>
        </Dropdown>

        <ExportExcelButton
          list={list}
          columns={bomColumns}
          defaultSelectedColumns={[t('bom.bomType'), t('bom.bomCode'), t('bom.bomName')]}
          dataMapper={bomDataMapper}
          fileName={`${t('bom.materialControlTemplate')}.xlsx`}
        />
        <ExportExcelButton
          isAll
          loadData={handleExportAll}
          columns={bomColumns}
          defaultSelectedColumns={[t('bom.bomType'), t('bom.bomCode'), t('bom.bomName')]}
          dataMapper={bomDataMapper}
          fileName={`${t('bom.materialControlTemplate')}.xlsx`}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("sale.orderSearchTip")}>
              <Input
                placeholder={t("sale.orderSearchTip")}
                style={{ width: 200 }}
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                onPressEnter={submit}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const columns: TableProps<Bom>["columns"] = [
    {
      title: t("bom.bomType"),
      key: "type",
      width: 80,
      render: (_, record) => <SText text={record.type} />
    },
    {
      title: t("bom.bomCode"),
      key: "code",
      width: 120,
      render: (_, record) => <SText text={record?.product?.code} />
    },
    {
      title: t("bom.bomName"),
      key: "name",
      width: 120,
      render: (_, record) => <SText text={record?.product?.name} />
    },
    {
      title: t("bom.bomUnit"),
      key: "unit",
      width: 80,
      render: (_, record) => (
        <SText text={record?.product?.uom?.toLocaleUpperCase()} />
      )
    },
    {
      title: t("product.brand1"),
      key: "unit",
      width: 80,
      render: (_, record) => <SText text={record?.product?.model || ""} />
    },
    {
      title: t("bom.bomVersion"),
      key: "version",
      width: 60,
      render: (_, record) => <SText text={record.version} />
    },
    {
      title: t("bom.bomVersionDesc"),
      key: "desc",
      width: 80,
      render: (_, record) => <SText text={record.versionDesc} />
    },
    {
      title: t("bom.bomStatus"),
      key: "status",
      width: 80,
      render: (_, record) => <SText text={record.status} />
    },
    {
      title: t("bom.approvaler"),
      key: "approvaler",
      width: 80,
      render: (_, record) => <SText text={record.approvaler} />
    },
    {
      title: t("bom.createDate"),
      dataIndex: "bomCreateDate",
      width: 100,
      render: text => (text ? dateFormat(text) : "")
    },
    {
      title: t("bom.creator"),
      key: "creator",
      width: 80,
      render: (_, record) => <SText text={record.bomCreateDateBy} />
    },
    {
      title: t("bom.updateDate"),
      dataIndex: "bomUpdateDate",
      width: 100,
      render: text => (text ? dateFormat(text) : "")
    },
    {
      title: t("bom.updater"),
      key: "updater",
      width: 80,
      render: (_, record) => <SText text={record.bomUpdateDateBy} />
    },
    {
      title: t("common.opt"),
      key: "detail",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Flex justify='space-evenly'>
          {/* <Button type="primary" icon={<EditOutlined />} onClick={() => setBomInfo(record)} /> */}
          <Button
            type="primary"
            icon={<GroupOutlined />}
            onClick={() => setDetailId(record.bomId)}
          />
          <SConModal title={t("common.rmTip")} content={t("common.rmContent")} onOk={() => delData(record.bomId)}>
            <Button
              type="link"
              icon={<DeleteOutlined />}
            ></Button>
          </SConModal>
        </Flex>
      )
    }
  ];

  const fetch = (offset: number, filters: BomFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList({ pagination: { offset, take }, body: filters.keyword }).catch(err => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  return (
    <BomWrap vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />

      {detailId && (
        <BomDetail
          open={!!detailId}
          id={detailId}
          onCancel={() => setDetailId("")}
        />
      )}

      {/* <BomUpdate 
        open={!!bomInfo} 
        detail={bomInfo} 
      /> */}
      {
        !!bomAddData && <BomAddForm data={bomAddData} onClose={() => setBomAddData(null)} onSucc={bom => {
          notification.success({ message: 'BOM' + t('app.addSuccess'), description: bom.product.code })
          addItem(bom);
          setBomAddData(null);
        }} />
      }
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </BomWrap>
  );
};

export default BomHome;
